import React, { useEffect, useRef, useState } from 'react';
import adminService from '../../services/adminService';
import { useDialog } from '../../context/DialogContext';
import { Modal } from '..';
import { Btn, Loading } from './shell';

/**
 * INVITE CARD — the admin sheet behind "DOWNLOAD INVITE CARD" on a user (⋯ menu / row action)
 * and on a Pro page. Mints the owner's referral code on open (idempotent), shows the link and
 * the back of the card with that code's QR, and hands over the two 4200 × 2400 print PNGs.
 *
 * `target` is `{ kind: 'user' | 'pro', id, name }` or null (closed). The card is the handoff's
 * 2A "the door": no name on it, every card differs only in its QR, so the preview is the
 * server's own render — never a client-side QR that could drift from the print.
 */
export default function InviteCardSheet({ target, onClose }) {
  const { showConfirm } = useDialog();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [copied, setCopied] = useState(false);
  const previewUrl = useRef(null);

  const kind = target?.kind;
  const id = target?.id;

  const loadPreview = async (k, i) => {
    const r = await adminService.inviteCardImage(k, i, 'back', true);
    if (!r.success) return;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = URL.createObjectURL(r.data);
    setPreview(previewUrl.current);
  };

  useEffect(() => {
    if (!kind || !id) return undefined;
    let live = true;
    setInfo(null); setError(''); setPreview(null); setCopied(false);
    (async () => {
      const r = await adminService.mintInviteCard(kind, id);
      if (!live) return;
      if (!r.success) { setError(r.error || 'Could not mint the code.'); return; }
      setInfo(r.data);
      loadPreview(kind, id);
    })();
    return () => {
      live = false;
      if (previewUrl.current) { URL.revokeObjectURL(previewUrl.current); previewUrl.current = null; }
    };
  }, [kind, id]);

  const download = async (side) => {
    setBusy(side);
    const r = await adminService.inviteCardImage(kind, id, side, false);
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not fetch the file.'); return; }
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portal-invite-${info?.code || 'card'}-${side}-4200x2400.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(info.url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* no clipboard */ }
  };

  const rotate = async () => {
    const yes = await showConfirm({
      title: 'REPLACE THIS CODE?',
      message: `Every card already printed with ${info.code} stops working. A fresh code is minted and the preview updates. Do this for a lost box of cards, not for a reprint.`,
      confirmLabel: 'REPLACE CODE',
      cancelLabel: 'KEEP IT',
      variant: 'danger',
      theme: 'admin',
    });
    if (!yes) return;
    setBusy('rotate');
    const r = await adminService.rotateInviteCard(kind, id);
    setBusy('');
    if (!r.success) { setError(r.error || 'Could not replace the code.'); return; }
    setInfo(r.data);
    setPreview(null);
    loadPreview(kind, id);
  };

  return (
    <Modal isOpen={!!target} onClose={onClose} title="INVITE CARD" size="lg">
      {target && (
        <div className="space-y-4" data-testid="invite-card-sheet">
          <p className="font-mono text-xs text-gray-600">
            A printed 3.5 × 2 in card for <strong className="font-mono">{target.name}</strong>. Nothing on it names them; the QR on the back is their personal link, and whoever scans it lands on their page after signing up.
          </p>
          {error && <p className="font-mono text-[11px] text-red-600">{error}</p>}
          {!info && !error && <Loading />}
          {info && (
            <>
              <div className="overflow-hidden rounded-[18px] bg-[#080612]" style={{ aspectRatio: '7 / 4' }} data-testid="invite-card-preview">
                {preview
                  ? <img src={preview} alt={`Back of the card with code ${info.code}`} className="block h-full w-full" />
                  : <div className="flex h-full items-center justify-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-white/60">RENDERING…</div>}
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[11px]">
                <div><span className="font-bold uppercase tracking-[0.1em] text-[#6B7280]">LINK </span><span data-testid="invite-card-url">{info.url}</span></div>
                <div><span className="font-bold uppercase tracking-[0.1em] text-[#6B7280]">SCANS </span>{info.scanCount}</div>
                <div><span className="font-bold uppercase tracking-[0.1em] text-[#6B7280]">SIGN-UPS </span>{info.referralCount}</div>
              </div>
              <p className="border-t border-gray-200 pt-2 font-mono text-[11px] text-gray-500">
                Print files are 4200 × 2400 (1200 dpi), no bleed — the printer adds 0.125 in of #080612 on every side. Test-scan one physical print before a bulk run.
              </p>
              <div className="flex flex-wrap justify-between gap-2 pt-1">
                <div className="flex flex-wrap gap-2">
                  <Btn variant="solid" onClick={() => download('front')} disabled={!!busy} data-testid="invite-card-front">{busy === 'front' ? 'FETCHING…' : 'DOWNLOAD FRONT'}</Btn>
                  <Btn variant="solid" onClick={() => download('back')} disabled={!!busy} data-testid="invite-card-back">{busy === 'back' ? 'FETCHING…' : 'DOWNLOAD BACK'}</Btn>
                  <Btn onClick={copy}>{copied ? 'COPIED' : 'COPY LINK'}</Btn>
                </div>
                <Btn variant="danger" onClick={rotate} disabled={!!busy} data-testid="invite-card-rotate">{busy === 'rotate' ? 'REPLACING…' : 'NEW CODE'}</Btn>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
